import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { Input } from '../atoms/Input/Input';
import { Alert } from '../atoms/Alert/Alert';

export interface AdminConfirmRequest {
  title: string;
  message: string;
  confirmLabel: string;
  /**
   * Strong confirmation for irreversible actions: the confirm button stays
   * disabled until this exact text (e.g. the account's e-mail) is typed.
   */
  requireText?: string;
  onConfirm: () => Promise<unknown>;
}

export interface AdminConfirmDialogProps {
  request: AdminConfirmRequest | null;
  onClose: () => void;
  /** Translated message shown in the dialog when the action failed. */
  errorMessage?: string;
  /** Extra controls in the dialog body (e.g. an amount field). */
  children?: ReactNode;
}

/**
 * Confirmation for every destructive admin action (ConfirmDialog: focus trap,
 * Escape, focus restore). Runs the action, keeps the dialog open with an Alert
 * when it fails, closes it on success.
 */
export function AdminConfirmDialog({ request, onClose, errorMessage, children }: AdminConfirmDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // ConfirmDialog re-runs its focus effect whenever onCancel changes identity
  // (moving focus to Cancel), so the callback must stay stable while typing.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const handleCancel = useCallback(() => {
    if (!busyRef.current) onCloseRef.current();
  }, []);

  useEffect(() => {
    setTyped('');
    setBusy(false);
    setFailed(false);
  }, [request]);

  if (request === null) return null;

  const needsText = request.requireText !== undefined;
  const textMatches = !needsText || typed.trim().toLowerCase() === request.requireText!.trim().toLowerCase();

  async function confirm(): Promise<void> {
    if (request === null || !textMatches) return;
    setBusy(true);
    setFailed(false);
    try {
      await request.onConfirm();
      onClose();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  const showBody = needsText || failed || children != null;

  return (
    <ConfirmDialog
      isOpen
      title={request.title}
      message={request.message}
      confirmLabel={request.confirmLabel}
      cancelLabel={t('common.cancel')}
      onConfirm={() => void confirm()}
      onCancel={handleCancel}
      confirmDisabled={!textMatches}
      isConfirming={busy}
    >
      {showBody ? (
        <>
          {children}
          {needsText && (
            <Input
              label={t('adminArea.confirm.typeToConfirm', { text: request.requireText })}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              fullWidth
            />
          )}
          {failed && (
            <Alert compact>{errorMessage ?? t('adminArea.errors.actionFailed')}</Alert>
          )}
        </>
      ) : undefined}
    </ConfirmDialog>
  );
}

export default AdminConfirmDialog;
